import React, { useCallback } from 'react';
import Div from '@/shared/Div';
import MenuNav from '@/shared/crisp/MenuNav';
import Map from '../Map';
import dlv from 'dlv';
import Heading from '@/shared/Heading';
import useScreen from '@/hooks/useScreen';
import SlickSlider from '@/shared/crisp/SlickSlider';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { E2E_SCORLL_CLASSES } from '@/constants/app';
import { useAppConfigContext } from '@/context/AppContext';
import { getSubCategoryURL } from '@/utils/urls';
import getSingleString from '@/utils/string';

type MainNavProps = {
  subCategories?: any;
  name?: string;
  className?: string;
};

function MainNav({ subCategories, name, className }: MainNavProps) {
  const router = useRouter();
  const { query } = router;
  const categoryId = query.categoryId;
  const {
    state: { isSCAClient },
  } = useAppConfigContext();

  const { isDesktop } = useScreen();
  const settings = {
    dots: false,
    infinite: false,
    slidesToShow: 8,
    slidesToScroll: subCategories?.length > 8 ? 8 : 1,
    centerMode: false,
    arrows: subCategories?.length > 8,
  };

  const renderBanner = useCallback((res: any, index: number) => {
    const { icon, name, subCategoryId } = dlv(res, 'attributes', {});
    return (
      <MenuNav
        image={icon}
        label={name}
        key={res.id}
        dataTestId={`menu${res.id}`}
        href={getSubCategoryURL(getSingleString(categoryId), subCategoryId, isSCAClient)}
      />
    );
  }, []);

  const midpoint = Math.ceil(subCategories?.length / 2);

  // Split the array into two parts only if subCategories > 3
  const shouldSplit = subCategories?.length > 3;
  const firstHalfSubCategories = shouldSplit ? subCategories.slice(0, midpoint) : subCategories;
  const secondHalfSubCategories = shouldSplit ? subCategories.slice(midpoint) : [];

  return (
    <Div className={classNames('md:py-s6', className)}>
      <Heading
        level={1}
        className="text-cti-primary text-h-1 md:text-desktop-display mb:s5 md:mb-s6"
      >
        {name}
      </Heading>
      {!isDesktop ? (
        <Div
          className={classNames(
            'block overflow-x-auto whitespace-nowrap hide-scrollbar leading-none lg:hidden',
            E2E_SCORLL_CLASSES,
          )}
        >
          <Map
            className="flex gap-s4"
            list={firstHalfSubCategories}
            data-testid="Main-nav"
            render={renderBanner}
            wrapper={'div'}
          />
          {shouldSplit && (
            <Map
              className="flex gap-s4"
              list={secondHalfSubCategories}
              data-testid="Main-nav"
              render={renderBanner}
              wrapper={'div'}
            />
          )}
        </Div>
      ) : null}
      {isDesktop ? (
        <SlickSlider
          data={subCategories}
          settings={settings}
          render={renderBanner}
          className="main-nav-slick"
          showProgress={settings.arrows}
          progressBarStyle="!w-full"
        />
      ) : null}
    </Div>
  );
}

export default React.memo(MainNav);
